import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  bookResultSchema,
  findSlotsResultSchema,
  holdResultSchema,
} from "@magarac/shared/schemas/availability";
import {
  calendarResultSchema,
  jobPageSchema,
  visitResponseSchema,
  type CalendarItem,
  type JobListItem,
} from "@magarac/shared/schemas/job";
import { addDays, localToUtc, todayIn, utcToLocal } from "@magarac/shared/time/tz";
import { Badge, Banner, Button, ChipGroup, Dialog, PageHeader, Spinner } from "@magarac/ui";
import { HelpIcon } from "../../components/HelpIcon";
import { api } from "../../lib/api";
import { useDragDrop, type DragPayload, type DropTarget } from "../../lib/drag";
import { conflictReasons, messageFor } from "../../lib/errors";
import {
  formatLocalDate,
  formatTime,
  formatWindow,
  newActionKey,
  VISIT_STATUS_LABEL,
} from "../../lib/format";
import { useSetting } from "../../lib/settings";
import { useTenant } from "../../lib/tenant";

export const Route = createFileRoute("/_app/calendar")({
  validateSearch: z.object({
    day: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    view: z.enum(["day", "week"]).optional(),
  }),
  component: CalendarPage,
});

const UNASSIGNED = "unassigned";

/**
 * Who does the visit after a drop. Dropping in its own lane (or another crew member's) only
 * changes the time; dropping a crew member's card on someone new swaps just that person; the
 * Unassigned lane takes everyone off.
 */
function laneAfterDrop(current: string[], fromLane: string | undefined, toLane: string): string[] {
  if (toLane === UNASSIGNED) return [];
  if (current.includes(toLane)) return current;
  if (fromLane && fromLane !== UNASSIGNED && current.includes(fromLane))
    return current.map((id) => (id === fromLane ? toLane : id));
  return [...current.filter((id) => id !== toLane), toLane].slice(-Math.max(1, current.length));
}
const PX_PER_MIN = 1.2;
const SNAP_MIN = 15;

interface PendingMove {
  visitId: string;
  label: string;
  start: string;
  resourceIds: string[];
  reasons: string[];
  /** When the screen loaded the visit; a newer change elsewhere refuses the move. */
  expectedUpdatedAt?: string;
}

/**
 * SCH-8, SCH-9: one lane per person plus "Unassigned", day and week views. Drag a visit to move it
 * or to hand it to someone else; drag a job from "Needs a time" to book it. Every drop is checked
 * by the availability engine; conflicts warn and the office can move it anyway.
 * Screen key: calendar
 */
function CalendarPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const scheduling = useSetting("scheduling");
  const hours = useSetting("hours");
  const tz = scheduling.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = todayIn(tz);
  const day = search.day ?? today;
  const view = search.view ?? "day";
  const weekStart = view === "week" ? mondayOf(day) : day;
  const from = weekStart;
  const to = view === "week" ? addDays(weekStart, 6) : day;
  const days = view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)) : [day];

  const cal = useQuery({
    queryKey: ["calendar", active?.id, from, to],
    queryFn: () => api.get("/calendar", calendarResultSchema, { query: { from, to } }),
    enabled: Boolean(active),
  });
  const unscheduled = useQuery({
    queryKey: ["jobs", active?.id, "list", "unscheduled", "calendar"],
    queryFn: () =>
      api.get("/jobs", jobPageSchema, { query: { status: "unscheduled", limit: 100 } }),
    enabled: Boolean(active),
  });

  const lanes = useMemo(
    () => [...(cal.data?.lanes ?? []), { resourceId: UNASSIGNED, name: "Unassigned", color: null }],
    [cal.data?.lanes],
  );
  const laneNames = new Map(lanes.map((l) => [l.resourceId, l.name]));

  // Visible hours: the business's earliest opening to latest closing (fallback 7 AM to 7 PM).
  const weekly = hours.value?.weekly ?? [];
  const dayStartMin = Math.max(0, Math.min(420, ...weekly.map((h) => h.startMin)) - 60);
  const dayEndMin = Math.min(1440, Math.max(1140, ...weekly.map((h) => h.endMin)) + 60);

  const [message, setMessage] = useState<{ tone: "ok" | "danger" | "warn"; text: string } | null>(
    null,
  );
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["calendar"] }),
      queryClient.invalidateQueries({ queryKey: ["jobs"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);
  };

  const move = useMutation({
    mutationFn: (m: Omit<PendingMove, "reasons" | "label"> & { force?: boolean }) =>
      api.patch(
        `/visits/${m.visitId}`,
        {
          start: m.start,
          resourceIds: m.resourceIds,
          ...(m.expectedUpdatedAt ? { expectedUpdatedAt: m.expectedUpdatedAt } : {}),
          ...(m.force ? { force: true } : {}),
        },
        visitResponseSchema,
      ),
    onSuccess: async () => {
      setPendingMove(null);
      setMessage(null);
      await refresh();
    },
    onError: (err, vars) => {
      const reasons = conflictReasons(err);
      if (reasons) {
        setPendingMove({ ...vars, label: pendingMove?.label ?? "This visit", reasons });
      } else {
        setPendingMove(null);
        setMessage({ tone: "danger", text: messageFor(err) });
        // Whatever went wrong, show the calendar as it really is now.
        void refresh();
      }
    },
  });

  const bookJob = useMutation({
    mutationFn: async ({
      job,
      target,
      minute,
    }: {
      job: JobListItem;
      target: DropTarget;
      minute: number | null;
    }) => {
      const lane = target.lane === UNASSIGNED ? undefined : target.lane;
      const slots = await api.post(
        "/availability/slots",
        {
          jobTypeId: job.jobTypeId,
          from: target.day,
          to: target.day,
          ...(job.propertyId ? { propertyId: job.propertyId } : {}),
          ...(lane ? { resourceId: lane } : {}),
        },
        findSlotsResultSchema,
      );
      if (slots.slots.length === 0)
        throw new Error(
          `No open time on ${formatLocalDate(target.day)}${lane ? ` for ${laneNames.get(lane)}` : ""}.`,
        );
      // The open window closest to where it was dropped (week view: the earliest).
      const wanted = minute === null ? null : localToUtc(target.day, minute, tz);
      const slot =
        wanted === null
          ? slots.slots[0]!
          : [...slots.slots].sort(
              (a, b) =>
                Math.abs(Date.parse(a.start) - wanted) - Math.abs(Date.parse(b.start) - wanted),
            )[0]!;
      const hold = await api.post(
        "/availability/hold",
        {
          jobTypeId: job.jobTypeId,
          start: slot.start,
          ...(job.propertyId ? { propertyId: job.propertyId } : {}),
          ...(lane ? { preferredResourceIds: [lane] } : {}),
        },
        holdResultSchema,
      );
      const booked = await api.post(
        "/availability/book",
        // Dropped on a person's lane: book onto that person in the same step, so a failure
        // can never leave a booked-but-unassigned visit behind (and a retry a duplicate).
        { holdId: hold.holdId, jobId: job.id, ...(lane ? { assign: true } : {}) },
        bookResultSchema,
        {
          idempotencyKey: newActionKey("cal-book"),
        },
      );
      return { job, start: booked.plannedStart };
    },
    onSuccess: async ({ job, start }) => {
      setMessage({
        tone: "ok",
        text: `Job #${job.number} booked for ${formatLocalDate(utcToLocal(Date.parse(start), tz).date)}, ${formatTime(start, tz)}.`,
      });
      await refresh();
    },
    onError: (err) => {
      setMessage({ tone: "danger", text: messageFor(err) });
      void refresh();
    },
  });

  const onDrop = (payload: DragPayload, target: DropTarget) => {
    setMessage(null);
    const minute = view === "day" ? snap(dayStartMin + target.offsetY / PX_PER_MIN) : null;
    if (payload.kind === "job") {
      const job = unscheduled.data?.items.find((j) => j.id === payload.id);
      if (job) bookJob.mutate({ job, target, minute });
      return;
    }
    const visit = cal.data?.items.find((v) => v.id === payload.id);
    if (!visit) return;
    // Week view keeps the time of day and changes the day; day view uses where it was dropped.
    const keepMinute = utcToLocal(Date.parse(visit.plannedStart), tz).minutes;
    const start = new Date(localToUtc(target.day, minute ?? keepMinute, tz)).toISOString();
    const resourceIds = laneAfterDrop(visit.resourceIds, payload.fromLane, target.lane);
    if (start === visit.plannedStart && sameIds(resourceIds, visit.resourceIds)) return;
    const expectedUpdatedAt = visit.updatedAt;
    setPendingMove({
      visitId: visit.id,
      label: payload.label,
      start,
      resourceIds,
      reasons: [],
      expectedUpdatedAt,
    });
    move.mutate({ visitId: visit.id, start, resourceIds, expectedUpdatedAt });
  };
  const { drag, handle } = useDragDrop(onDrop);

  const go = (next: { day?: string; view?: "day" | "week" }) =>
    void navigate({ to: "/calendar", search: { day: next.day ?? day, view: next.view ?? view } });
  const step = view === "week" ? 7 : 1;

  return (
    <div className="mx-auto max-w-[1400px]">
      <PageHeader
        title="Calendar"
        subtitle={view === "week" ? `Week of ${formatLocalDate(weekStart)}` : formatLocalDate(day)}
        help={<HelpIcon screen="calendar" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              aria-label="Previous"
              onClick={() => go({ day: addDays(day, -step) })}
            >
              ‹
            </Button>
            <Button variant="secondary" onClick={() => go({ day: today })}>
              Today
            </Button>
            <Button
              variant="secondary"
              aria-label="Next"
              onClick={() => go({ day: addDays(day, step) })}
            >
              ›
            </Button>
            <ChipGroup
              label="View"
              value={view}
              onChange={(v) => go({ view: v })}
              options={[
                { value: "day", label: "Day" },
                { value: "week", label: "Week" },
              ]}
            />
          </div>
        }
      />
      {message ? <Banner tone={message.tone}>{message.text}</Banner> : null}
      {move.isPending || bookJob.isPending ? (
        <div className="mb-2 flex items-center gap-2 text-sm text-muted">
          <Spinner size="sm" /> Checking the schedule...
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <div className="min-w-0 overflow-x-auto rounded-card border border-line bg-surface">
          {cal.isLoading ? (
            <div className="p-6">
              <Spinner />
            </div>
          ) : view === "day" ? (
            <DayGrid
              day={day}
              tz={tz}
              lanes={lanes}
              items={cal.data?.items ?? []}
              startMin={dayStartMin}
              endMin={dayEndMin}
              handle={handle}
              dropTarget={drag?.target ?? null}
            />
          ) : (
            <WeekGrid
              days={days}
              tz={tz}
              lanes={lanes}
              items={cal.data?.items ?? []}
              handle={handle}
              dropTarget={drag?.target ?? null}
              today={today}
              onPickDay={(d) => go({ day: d, view: "day" })}
            />
          )}
        </div>
        <aside aria-label="Jobs that need a time">
          <h2 className="mb-2 font-semibold">Needs a time</h2>
          <p className="mb-2 text-xs text-muted">
            Drag a job onto someone's day, or open it to pick a time.
          </p>
          <ul className="space-y-2">
            {unscheduled.data?.items.map((j) => (
              <li key={j.id}>
                <Link
                  to="/jobs/$id"
                  params={{ id: j.id }}
                  {...handle({ kind: "job", id: j.id, label: `Job #${j.number}` })}
                  className="block cursor-grab rounded-lg border border-line bg-surface p-3 text-sm hover:bg-brand-50"
                >
                  <div className="font-medium">
                    #{j.number} · {j.customerName}
                  </div>
                  <div className="text-muted">
                    {j.jobTypeName}
                    {j.dueOn ? ` · due ${formatLocalDate(j.dueOn)}` : ""}
                  </div>
                  {j.urgency === "urgent" ? <Badge tone="danger">Urgent</Badge> : null}
                </Link>
              </li>
            ))}
            {unscheduled.data?.items.length === 0 ? (
              <li className="text-sm text-muted">Everything is scheduled.</li>
            ) : null}
          </ul>
        </aside>
      </div>

      {drag ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-50 rounded-lg bg-brand-600 px-3 py-2 text-sm text-white shadow-lg"
          style={{ left: drag.x + 12, top: drag.y + 12 }}
        >
          {drag.payload.label}
          {drag.target ? ` → ${laneNames.get(drag.target.lane) ?? ""}` : ""}
        </div>
      ) : null}

      <Dialog
        open={Boolean(pendingMove && pendingMove.reasons.length > 0)}
        onClose={() => setPendingMove(null)}
        title="This move has a conflict"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingMove(null)}>
              Leave it where it was
            </Button>
            <Button
              busy={move.isPending}
              onClick={() =>
                pendingMove &&
                move.mutate({
                  visitId: pendingMove.visitId,
                  start: pendingMove.start,
                  resourceIds: pendingMove.resourceIds,
                  expectedUpdatedAt: pendingMove.expectedUpdatedAt,
                  force: true,
                })
              }
            >
              Move it anyway
            </Button>
          </>
        }
      >
        <ul className="list-disc space-y-1 pl-5">
          {pendingMove?.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </Dialog>
    </div>
  );
}

type Handle = ReturnType<typeof useDragDrop>["handle"];

function DayGrid({
  day,
  tz,
  lanes,
  items,
  startMin,
  endMin,
  handle,
  dropTarget,
}: {
  day: string;
  tz: string;
  lanes: Array<{ resourceId: string; name: string }>;
  items: CalendarItem[];
  startMin: number;
  endMin: number;
  handle: Handle;
  dropTarget: DropTarget | null;
}) {
  const height = (endMin - startMin) * PX_PER_MIN;
  const hours: number[] = [];
  for (let m = Math.ceil(startMin / 60) * 60; m < endMin; m += 60) hours.push(m);
  return (
    <div className="flex min-w-max">
      <div className="sticky left-0 z-10 w-16 shrink-0 border-r border-line bg-surface">
        <div className="h-10 border-b border-line" />
        <div className="relative" style={{ height }}>
          {hours.map((m) => (
            <div
              key={m}
              className="absolute right-2 -translate-y-2 text-xs text-muted"
              style={{ top: (m - startMin) * PX_PER_MIN }}
            >
              {labelForMinute(m)}
            </div>
          ))}
        </div>
      </div>
      {lanes.map((lane) => {
        const laneItems = items.filter((v) =>
          lane.resourceId === UNASSIGNED
            ? !v.resourceIds.some((id) => lanes.some((l) => l.resourceId === id))
            : v.resourceIds.includes(lane.resourceId),
        );
        const over = dropTarget?.lane === lane.resourceId && dropTarget.day === day;
        return (
          <div key={lane.resourceId} className="w-48 shrink-0 border-r border-line last:border-r-0">
            <div className="flex h-10 items-center border-b border-line px-2 text-sm font-semibold">
              {lane.name}
            </div>
            <div
              data-drop-lane={lane.resourceId}
              data-drop-day={day}
              data-lane-name={lane.name}
              className={`relative ${over ? "bg-brand-50" : lane.resourceId === UNASSIGNED ? "bg-canvas" : ""}`}
              style={{ height }}
            >
              {hours.map((m) => (
                <div
                  key={m}
                  className="absolute inset-x-0 border-t border-line/70"
                  style={{ top: (m - startMin) * PX_PER_MIN }}
                />
              ))}
              {laneItems.map((v) => {
                const s = utcToLocal(Date.parse(v.plannedStart), tz);
                const e = utcToLocal(Date.parse(v.plannedEnd), tz);
                const startM = s.date === day ? s.minutes : startMin;
                const endM = e.date === day ? e.minutes : endMin;
                return (
                  <VisitBlock
                    key={v.id}
                    item={v}
                    lane={lane.resourceId}
                    tz={tz}
                    handle={handle}
                    style={{
                      top: Math.max(0, (startM - startMin) * PX_PER_MIN),
                      height: Math.max(36, (endM - startM) * PX_PER_MIN),
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekGrid({
  days,
  tz,
  lanes,
  items,
  handle,
  dropTarget,
  today,
  onPickDay,
}: {
  days: string[];
  tz: string;
  lanes: Array<{ resourceId: string; name: string }>;
  items: CalendarItem[];
  handle: Handle;
  dropTarget: DropTarget | null;
  today: string;
  onPickDay: (day: string) => void;
}) {
  return (
    <table className="w-full min-w-[900px] table-fixed border-collapse text-sm">
      <thead>
        <tr>
          <th className="w-32 border-b border-line p-2 text-left font-medium text-muted">Who</th>
          {days.map((d) => (
            <th
              key={d}
              className={`border-b border-l border-line p-2 text-left font-medium ${d === today ? "text-brand-700" : "text-muted"}`}
            >
              <button type="button" className="underline" onClick={() => onPickDay(d)}>
                {formatLocalDate(d)}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lanes.map((lane) => (
          <tr key={lane.resourceId}>
            <th scope="row" className="border-b border-line p-2 text-left align-top font-semibold">
              {lane.name}
            </th>
            {days.map((d) => {
              const cell = items.filter(
                (v) =>
                  utcToLocal(Date.parse(v.plannedStart), tz).date === d &&
                  (lane.resourceId === UNASSIGNED
                    ? !v.resourceIds.some((id) => lanes.some((l) => l.resourceId === id))
                    : v.resourceIds.includes(lane.resourceId)),
              );
              const over = dropTarget?.lane === lane.resourceId && dropTarget.day === d;
              return (
                <td
                  key={d}
                  data-drop-lane={lane.resourceId}
                  data-drop-day={d}
                  className={`h-24 border-b border-l border-line p-1 align-top ${over ? "bg-brand-50" : lane.resourceId === UNASSIGNED ? "bg-canvas" : ""}`}
                >
                  <div className="space-y-1">
                    {cell.map((v) => (
                      <VisitBlock
                        key={v.id}
                        item={v}
                        lane={lane.resourceId}
                        tz={tz}
                        handle={handle}
                        compact
                      />
                    ))}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** SCH-9 card: customer, address, service, window, status, crew, drive time. Click opens the job. */
function VisitBlock({
  item,
  lane,
  tz,
  handle,
  style,
  compact,
}: {
  item: CalendarItem;
  lane: string;
  tz: string;
  handle: Handle;
  style?: React.CSSProperties;
  compact?: boolean;
}) {
  const status = VISIT_STATUS_LABEL[item.status];
  const movable = ["scheduled", "confirmed"].includes(item.status);
  const drag = movable
    ? handle({
        kind: "visit",
        id: item.id,
        label: `${item.customerName}, job #${item.jobNumber}`,
        fromLane: lane,
      })
    : null;
  return (
    <Link
      to="/jobs/$id"
      params={{ id: item.jobId }}
      {...(drag ?? {})}
      style={{ ...(drag?.style ?? {}), ...(style ?? {}) }}
      title={`${item.customerName}\n${item.jobTypeName}\n${formatWindow(item, tz)}${item.address ? `\n${item.address.line1}, ${item.address.city}` : ""}`}
      className={`${compact ? "block" : "absolute inset-x-1"} overflow-hidden rounded-md border-l-4 bg-brand-50 p-1.5 text-xs text-ink shadow-sm hover:bg-brand-100 ${
        item.urgency === "urgent" ? "border-danger" : "border-brand-600"
      } ${movable ? "cursor-grab" : "opacity-80"}`}
    >
      <div className="truncate font-semibold">{item.customerName}</div>
      <div className="truncate">
        {formatWindow(item, tz)} · {item.jobTypeName}
      </div>
      {!compact ? (
        <>
          {item.address ? <div className="truncate text-muted">{item.address.line1}</div> : null}
          <div className="truncate text-muted">
            {status.label}
            {item.crewSize > 1 ? ` · crew of ${item.crewSize}` : ""}
            {item.travelPadMin > 0 ? ` · ${item.travelPadMin} min drive` : ""}
          </div>
        </>
      ) : null}
    </Link>
  );
}

function snap(min: number): number {
  return Math.max(0, Math.min(1440 - SNAP_MIN, Math.round(min / SNAP_MIN) * SNAP_MIN));
}

function labelForMinute(m: number): string {
  const h = Math.floor(m / 60);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12} ${suffix}`;
}

function mondayOf(date: string): string {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  return addDays(date, -((weekday + 6) % 7));
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}
